import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";

/**
 * Grupy umów ramowych (AgreementGroup) — np. „budowlanka kujawsko-pomorskie”,
 * „geodeci kujawsko-pomorskie”. Jedna umowa może być w wielu grupach; umowa bez
 * grupy trafia w pickerach do syntetycznego koszyka „Bez przypisanej grupy”
 * (zob. NO_GROUP_LABEL), którego backend NIE zna.
 *
 * Odczyt: każdy pracownik. Zapis (grupy i przypisania): CEO, dyrektor techniczny
 * i osoby z flagą „Grupy umów ramowych” — zob. canManageAgreementGroups().
 */
export interface AgreementGroupBrief {
  id: string;
  name: string;
}

export interface AgreementGroup extends AgreementGroupBrief {
  description: string;
  agreements_count: number;
  created_at: string;
  updated_at: string;
}

export const NO_GROUP_LABEL = "Bez przypisanej grupy";
export const NO_GROUP_KEY = "__none__";

export const agreementGroupsKey = ["agreement-groups"] as const;

export const useAgreementGroups = (enabled = true) =>
  useQuery({
    queryKey: agreementGroupsKey,
    queryFn: () =>
      apiClient
        .get<{ data: AgreementGroup[] }>("/agreement-groups/")
        .then((r) => r.data.data),
    staleTime: 5 * 60_000,
    enabled,
  });

/** Every query that renders group names/badges — invalidated after any group write. */
const invalidateGroupConsumers = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: agreementGroupsKey });
  qc.invalidateQueries({ queryKey: ["agreements"] });
  qc.invalidateQueries({ queryKey: ["agreement"] });
  qc.invalidateQueries({ queryKey: ["est", "framework-agreements"] });
};

export const useAgreementGroupMutations = () => {
  const qc = useQueryClient();
  const done = () => invalidateGroupConsumers(qc);
  const create = useMutation({
    mutationFn: (payload: { name: string; description?: string }) =>
      apiClient
        .post<{ data: AgreementGroup }>("/agreement-groups/", payload)
        .then((r) => r.data.data),
    onSuccess: done,
  });
  const update = useMutation({
    mutationFn: ({ id, ...payload }: { id: string; name?: string; description?: string }) =>
      apiClient
        .patch<{ data: AgreementGroup }>(`/agreement-groups/${id}/`, payload)
        .then((r) => r.data.data),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/agreement-groups/${id}/`),
    onSuccess: done,
  });
  return { create, update, remove };
};

/** PUT the FULL set of group ids for one agreement (empty list = no group). */
export const useSetAgreementGroups = (agreementId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupIds: string[]) =>
      apiClient
        .put(`/agreements/${agreementId}/groups/`, { group_ids: groupIds })
        .then((r) => r.data.data),
    onSuccess: () => invalidateGroupConsumers(qc),
  });
};

/**
 * Bucket rows by group for the „rozwijane grupy → podwykonawcy" pickers.
 * A row with several groups appears under each of them; rows without any group
 * land in the trailing NO_GROUP_KEY bucket. Groups are sorted by name (pl).
 */
export function groupRows<T extends { groups?: AgreementGroupBrief[] | null }>(
  rows: T[],
): { key: string; name: string; rows: T[] }[] {
  const buckets = new Map<string, { key: string; name: string; rows: T[] }>();
  const none: T[] = [];
  for (const row of rows) {
    const groups = row.groups ?? [];
    if (groups.length === 0) {
      none.push(row);
      continue;
    }
    for (const g of groups) {
      let b = buckets.get(g.id);
      if (!b) {
        b = { key: g.id, name: g.name, rows: [] };
        buckets.set(g.id, b);
      }
      b.rows.push(row);
    }
  }
  const out = [...buckets.values()].sort((a, b) => a.name.localeCompare(b.name, "pl"));
  if (none.length) out.push({ key: NO_GROUP_KEY, name: NO_GROUP_LABEL, rows: none });
  return out;
}

/** Axios error → first human-readable message (DRF field errors or detail). */
export function groupErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return String(data[0] ?? fallback);
  if (typeof data === "object") {
    const first = Object.values(data as Record<string, unknown>)[0];
    if (Array.isArray(first)) return String(first[0] ?? fallback);
    if (typeof first === "string") return first;
  }
  return fallback;
}
